import {
  PayableRow,
  PayableStatus,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payablesApi } from "@api";
import { queriesPayableRows, queriesPayables } from "@query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";

const invalidateAfterMutation = (successItems: PayableRow[]) => {
  WSQueryCache.invalidateQueries(
    queriesPayableRows.searchPayableRows.queryKeyBase
  );
  WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
};

const processUnapprovePayables = (
  items: PayableRow[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<PayableRow>({
    onProgress,
    action: async item => {
      if (!item.id) {
        throw new Error("Payable is missing id");
      }
      if (item.status === PayableStatus.PAID) {
        throw new Error("Cannot unapprove paid payable");
      }
      return payablesApi.updatePayableWorkflowStatus({
        payableId: item.id,
        updatePayableWorkflowStatusRequest: {
          payerApprovalStatus:
            UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PENDING
        }
      });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkUnapprovePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayableRow[], onSuccess?: () => void) =>
    runBulkAction({
      actionName: "Unapprove payables",
      itemCount: items.length,
      confirmDescription: "Unapprove payables from being paid",
      confirmLabel: "Unapprove",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processUnapprovePayables(data, onProgress, onSuccess)
    });
};
